import { PendingSyncIndicator } from '@/components/offline/PendingSyncIndicator';
import { Bell, Settings, MapPin, XCircle, Check, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { getPreferredNavigationProvider, setPreferredNavigationProvider } from '@/lib/navigation';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { HtmlContent } from '@/components/ui/html-content';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { userManager, useSchool, isMasterAdmin, useNotifications, useAuthStore, useMarkNotificationRead, useMarkAllNotificationsRead } from '@sportschool/shared-logic';
import { confirmAndLogout } from '@/lib/logout';
import i18n from '../i18n/i18n';
import { SchoolSwitcher } from './SchoolSwitcher';
import { SkillLevelBadge } from '@/components/common/SkillLevelBadge';
import { ThemeSwitcher } from '@/components/common/ThemeSwitcher';
import type { User } from 'oidc-client-ts';

export function TopAppBar() {
  const { t } = useTranslation();
  const { schoolId } = useParams<{ schoolId: string }>();
  const activeSchoolId = schoolId || localStorage.getItem('active_school_id') || '';
  const [user, setUser] = useState<User | null>(null);
  const userContext = useAuthStore((s) => s.userContext);
  const activeRole = useAuthStore((s) => s.activeRole);
  const setActiveRole = useAuthStore((s) => s.setActiveRole);
  const navigate = useNavigate();

  const currentLang = i18n.language?.startsWith('pl') ? 'pl' : 'en';

  useEffect(() => {
    userManager.getUser().then((u) => {
      setUser(u);
    });
  }, []);

  const [preferredNav, setPreferredNav] = useState(getPreferredNavigationProvider());

  const { data: school } = useSchool(activeSchoolId);
  const { data: notifications } = useNotifications(activeSchoolId);
  const markRead = useMarkNotificationRead(activeSchoolId);
  const markAllRead = useMarkAllNotificationsRead(activeSchoolId);

  const unreadCount = useMemo(() => {
    return notifications?.filter((n) => !n.isRead).length ?? 0;
  }, [notifications]);

  const email = userContext?.user?.email || user?.profile?.email || '';
  const firstName = userContext?.user?.firstName || user?.profile?.given_name || '';
  const lastName = userContext?.user?.lastName || user?.profile?.family_name || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || user?.profile?.name || email;

  // Display initials: First letter of first name and first letter of last name
  const initials = useMemo(() => {
    const f = firstName ? firstName.charAt(0) : '';
    const l = lastName ? lastName.charAt(0) : '';
    if (f || l) return (f + l).toUpperCase();

    const nameStr = user?.profile?.name || email;
    if (nameStr) {
      const parts = nameStr.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
      }
      return nameStr.charAt(0).toUpperCase();
    }
    return 'U';
  }, [firstName, lastName, user, email]);

  const isMaster = useMemo(() => isMasterAdmin(user?.access_token), [user]);

  const schoolRoleStr = useMemo(() => {
    // Try roles from userContext (string[])
    const contextRoles = userContext?.activeSchool?.roles;
    if (Array.isArray(contextRoles) && contextRoles.length > 0) {
      const list = [];
      if (contextRoles.includes('Admin')) list.push(t('users.roles.admin'));
      if (contextRoles.includes('Coach')) list.push(t('users.roles.coach'));
      if (contextRoles.includes('Student')) list.push(t('users.roles.student'));
      return list.join(', ');
    }

    return '';
  }, [userContext?.activeSchool?.roles, t]);

  const roleLabel = useMemo(() => {
    if (isMaster) return t('nav.masterAdmin');
    if (activeRole) {
      if (activeRole === 'Admin') return t('users.roles.admin');
      if (activeRole === 'Coach') return t('users.roles.coach');
      if (activeRole === 'Student') return t('users.roles.student');
    }
    return schoolRoleStr || t('users.roles.student');
  }, [isMaster, activeRole, schoolRoleStr, t]);

  const profileUrl = useMemo(() => {
    if (!activeSchoolId) return undefined;
    return activeRole === 'Student'
      ? `/schools/${activeSchoolId}/settings`
      : `/schools/${activeSchoolId}/student`;
  }, [activeSchoolId, activeRole]);

  const toggleLanguage = () => {
    i18n.changeLanguage(currentLang === 'en' ? 'pl' : 'en');
  };

  return (
    <header className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b flex justify-between items-center px-6 py-3 shrink-0 gap-4">
      <div className={cn("flex items-center gap-2 md:hidden min-w-0", activeRole === 'Admin' && "pl-12")}>
        <span className="text-lg font-bold text-primary truncate">{school?.name ?? 'SportSchool'}</span>
      </div>

      <div className="hidden md:flex flex-1" />

      <div className="flex items-center gap-2 flex-1 md:flex-none justify-end">

        <PendingSyncIndicator />

        <SchoolSwitcher />

        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          className="text-xs font-semibold uppercase tracking-wide hidden sm:flex"
        >
          {currentLang === 'en' ? 'PL' : 'EN'}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative cursor-pointer">
              <Bell className="size-4" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 size-1.5 bg-destructive rounded-full" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 mt-1 rounded-xl shadow-lg border p-1.5 bg-popover text-popover-foreground max-h-96 overflow-y-auto no-scrollbar">
            <div className="flex items-center justify-between px-2.5 py-1.5 border-b">
              <span className="font-semibold text-xs text-muted-foreground uppercase tracking-wider">
                {t('dashboard.notifications', 'Powiadomienia')} ({unreadCount})
              </span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    markAllRead.mutate();
                  }}
                  disabled={markAllRead.isPending}
                  className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-1 cursor-pointer disabled:opacity-50 transition-colors"
                  title={t('common.markAllRead', 'Oznacz wszystkie jako przeczytane')}
                >
                  <CheckCheck className="size-3.5" />
                  <span>{t('common.markAllReadShort', 'Oznacz wszystkie')}</span>
                </button>
              )}
            </div>
            {notifications && notifications.length > 0 ? (
              <div className="flex flex-col py-1">
                {notifications.slice(0, 5).map((n) => (
                  <DropdownMenuItem
                    key={n.id}
                    onClick={() => {
                      if (!n.isRead) {
                        markRead.mutate(n.id);
                      }
                      if (n.payload) {
                        let targetPath = n.payload;

                        // Handle JSON payloads (e.g. {"unavailabilityId":"..."})
                        if (targetPath.trim().startsWith('{')) {
                          try {
                            const parsed = JSON.parse(targetPath);
                            if (parsed.unavailabilityId) {
                              targetPath = `/schools/${activeSchoolId}/dashboard`;
                            } else if (parsed.scheduleId && activeRole !== 'Student') {
                              targetPath = `/schools/${activeSchoolId}/schedules/${parsed.scheduleId}`;
                            } else if (parsed.sessionId) {
                              if (activeRole === 'Student') {
                                targetPath = `/schools/${activeSchoolId}/student`;
                              } else {
                                targetPath = `/schools/${activeSchoolId}/sessions/${parsed.sessionId}`;
                              }
                            } else if (parsed.substitutionId) {
                              targetPath = `/schools/${activeSchoolId}/coach`;
                            }
                          } catch (e) {
                            console.error('Failed to parse notification payload JSON:', e);
                          }
                        }
                        // Handle prefix payloads (e.g. session:ID)
                        else if (targetPath.startsWith('session:')) {
                          const sessionId = targetPath.split(':')[1];
                          if (activeRole === 'Student') {
                            targetPath = `/schools/${activeSchoolId}/student`;
                          } else {
                            targetPath = `/schools/${activeSchoolId}/sessions/${sessionId}`;
                          }
                        }

                        if (targetPath && !targetPath.startsWith('{') && !targetPath.startsWith('session:')) {
                          navigate(targetPath);
                        }
                      }
                    }}
                    className="flex flex-col items-start gap-1 p-2.5 rounded-lg hover:bg-accent cursor-pointer transition-colors focus:bg-accent focus:text-accent-foreground outline-none border-b last:border-0 border-border/40"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <span className={`size-1.5 rounded-full shrink-0 ${n.isRead ? 'bg-transparent' : 'bg-primary'}`} />
                      <p className="text-xs font-semibold text-foreground truncate flex-1">{n.title}</p>
                      <span className="text-[9px] text-muted-foreground shrink-0 font-medium">
                        {new Date(n.createdAt).toLocaleString(
                          i18n.language?.startsWith('pl') ? 'pl-PL' : 'en-US',
                          {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )}
                      </span>
                      {!n.isRead && (
                        <button
                          type="button"
                          className="size-5 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 rounded shrink-0 transition-colors cursor-pointer"
                          title={t('common.markAsRead', 'Oznacz jako przeczytane')}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            markRead.mutate(n.id);
                          }}
                          disabled={markRead.isPending && markRead.variables === n.id}
                        >
                          <Check className="size-3.5" />
                        </button>
                      )}
                    </div>
                    <HtmlContent html={n.body} className="text-[11px] text-muted-foreground pl-3.5 leading-normal break-words" />
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="-mx-1.5 my-1" />
                <DropdownMenuItem asChild>
                  <Link
                    to={`/schools/${activeSchoolId}/notifications`}
                    className="w-full flex items-center justify-center text-center text-xs font-bold text-primary hover:underline py-1.5 cursor-pointer bg-transparent"
                  >
                    {t('dashboard.showAllNotifications', 'Pokaż wszystkie')}
                  </Link>
                </DropdownMenuItem>
              </div>
            ) : (
              <div className="py-6 text-center text-xs text-muted-foreground italic">
                {t('dashboard.noNotifications', 'Brak nowych powiadomień.')}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {activeRole === 'Admin' && (
          <Button variant="ghost" size="icon" className="hidden sm:flex" asChild>
            <Link to={activeSchoolId ? `/schools/${activeSchoolId}/settings` : '/settings'}>
              <Settings className="size-4" />
            </Link>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Avatar className="size-8 cursor-pointer hover:opacity-90 transition-opacity">
              <AvatarFallback className="text-xs bg-primary text-primary-foreground font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 mt-1 rounded-xl shadow-lg border p-1.5 bg-popover text-popover-foreground">
            <DropdownMenuLabel className="font-normal px-2.5 py-2">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-semibold leading-none text-foreground">{fullName}</p>
                <p className="text-xs leading-none text-muted-foreground truncate">{email}</p>
              </div>
            </DropdownMenuLabel>
            
            {roleLabel && (
              <>
                <DropdownMenuSeparator className="-mx-1.5 my-1" />
                <div className="px-2.5 py-2 text-xs text-muted-foreground flex flex-col gap-1">
                  <span className="font-medium text-[10px] uppercase tracking-wider text-muted-foreground/75">
                    {t('userMenu.role')}
                  </span>
                  <div className="flex flex-wrap gap-1.5 items-center mt-0.5">
                    <span className="font-medium text-foreground bg-primary/10 text-primary w-max px-2.5 py-0.5 rounded-full text-[11px]">
                      {roleLabel}
                    </span>
                    {userContext?.activeSchool?.skillLevelName && (
                      <SkillLevelBadge
                        levelName={userContext.activeSchool.skillLevelName}
                        size="sm"
                      />
                    )}
                  </div>
                </div>
              </>
            )}

            {userContext?.activeSchool?.roles && userContext.activeSchool.roles.length > 1 && (
              <>
                <DropdownMenuSeparator className="-mx-1.5 my-1" />
                <div className="px-2.5 py-2 text-xs text-muted-foreground flex flex-col gap-1 select-none">
                  <span className="font-semibold text-[10px] uppercase tracking-wider text-muted-foreground/75 mb-1">
                    {t('userMenu.changeRole', 'Zmień aktywną rolę')}
                  </span>
                  <div className="flex flex-col gap-1 mt-0.5">
                    {userContext.activeSchool.roles.map((r) => {
                      const isActive = activeRole === r;
                      let label = r;
                      if (r === 'Admin') label = t('users.roles.admin');
                      if (r === 'Coach') label = t('users.roles.coach');
                      if (r === 'Student') label = t('users.roles.student');

                      return (
                        <button
                          key={r}
                          onClick={() => {
                            setActiveRole(r);
                            if (r === 'Student') {
                              navigate(`/schools/${activeSchoolId}/student`);
                            } else if (r === 'Coach') {
                              navigate(`/schools/${activeSchoolId}/coach`);
                            } else {
                              navigate(`/schools/${activeSchoolId}/dashboard`);
                            }
                          }}
                          className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left transition-all ${
                            isActive
                              ? 'bg-primary/10 text-primary font-bold'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer border-0 bg-transparent w-full'
                          }`}
                        >
                          <span>{label}</span>
                          {isActive && <span className="text-[10px]">●</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
            
            {profileUrl && (
              <>
                <DropdownMenuSeparator className="-mx-1.5 my-1" />
                <DropdownMenuItem asChild>
                  <Link to={profileUrl} className="cursor-pointer w-full flex items-center px-2.5 py-2 rounded-lg hover:bg-accent hover:text-accent-foreground text-sm font-medium transition-colors">
                    {t('userMenu.profile')}
                  </Link>
                </DropdownMenuItem>
              </>
            )}
            
            <DropdownMenuSeparator className="-mx-1.5 my-1 sm:hidden" />
            <DropdownMenuItem
              onClick={toggleLanguage}
              className="cursor-pointer w-full flex items-center justify-between px-2.5 py-2 rounded-lg hover:bg-accent hover:text-accent-foreground text-sm font-medium transition-colors sm:hidden"
            >
              <span>{t('userMenu.language', 'Język')}</span>
              <span className="font-extrabold uppercase bg-primary/10 text-primary px-2.5 py-0.5 rounded-md text-[10px]">
                {currentLang === 'pl' ? 'Polski (PL)' : 'English (EN)'}
              </span>
            </DropdownMenuItem>

            <DropdownMenuSeparator className="-mx-1.5 my-1" />
            <div className="px-2.5 py-1.5 space-y-1.5">
              <span className="block text-xs font-semibold text-muted-foreground">{t('userMenu.theme')}</span>
              <ThemeSwitcher />
            </div>

            {preferredNav && (
              <>
                <DropdownMenuSeparator className="-mx-1.5 my-1" />
                <DropdownMenuItem
                  onClick={() => {
                    setPreferredNavigationProvider(null);
                    setPreferredNav(null);
                    toast.info(t('navigation.defaultCleared', 'Wyczyszczono domyślny wybór nawigacji'));
                  }}
                  className="cursor-pointer w-full flex flex-col items-start gap-1 px-2.5 py-2 rounded-lg hover:bg-accent hover:text-accent-foreground text-sm transition-colors"
                >
                  <div className="flex items-center gap-2 w-full">
                    <MapPin className="size-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{t('navigation.defaultApp', 'Domyślna nawigacja')}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {preferredNav === 'google' ? 'Google Maps' : preferredNav === 'waze' ? 'Waze' : 'Apple Maps'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-destructive bg-destructive/10 px-2 py-1 rounded-md mt-1 self-end">
                    <XCircle className="size-3" />
                    {t('navigation.clear', 'Wyczyść')}
                  </div>
                </DropdownMenuItem>
              </>
            )}
            
            <DropdownMenuSeparator className="-mx-1.5 my-1" />
            <DropdownMenuItem
              onClick={() => void confirmAndLogout()}
              className="cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive w-full px-2.5 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {t('nav.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
